import { NavLink, useLocation } from 'react-router-dom'

import {
  Badge,
  ChartColumnIcon,
  CalendarIcon,
  CriticalIcon,
  ExternalLinkIcon,
  MessagesSquareIcon,
  NewBadge,
  PaintBucketIcon,
  Sidebar,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import {
  useIsOneBufferOrg,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { useCommunityCountsContext } from '~publish/contexts/CommunityCountsContext'
import { useCommunityCounter } from '~publish/contexts/useCommunityCounter'
import { buildStartPageEditorUrl } from '~publish/helpers/externalUrls'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { formatCompactNumber } from '~publish/helpers/numberFormatters'
import { usePostCounts } from '~publish/pages/AllChannels/PostList/usePostCounts'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'

import type { MainSidebarChannel } from './useChannels'
import { useChannelUrl } from '../../hooks/useChannelUrl'
import { useCommentsChannelUrl } from '../../hooks/useCommentsChannelUrl'
import { useInsightsUrl } from '../../hooks/useInsightsUrl'

import styles from './ChannelSubNav.module.css'

import type { JSX } from 'react'

type ChannelSubNavProps = {
  channel: MainSidebarChannel
  handleNavigation: (channelId: string) => void
}

/**
 * Helper function to determine the suffix for the Community navigation item
 */
function getCommunitySuffix({
  isLoading,
  count,
  includesMentions,
  needsRefresh,
}: {
  isLoading: boolean
  count: number
  includesMentions: boolean
  needsRefresh: boolean
}): JSX.Element | undefined {
  // Priority 1: Needs refresh indicator
  if (needsRefresh) {
    return (
      <span
        className={styles.errorIndicator}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The named channel error indicator is rendered from an icon component, not an img source.
        role="img"
        aria-label="There is a problem with this channel, you may need to refresh it."
      >
        <CriticalIcon color="critical" />
      </span>
    )
  }

  // Priority 2: Loading state
  if (isLoading) {
    return (
      <>
        &ensp;
        <VisuallyHidden>Comment counts loading</VisuallyHidden>
      </>
    )
  }

  // Priority 2: Community count badge (only if count > 0)
  if (count > 0) {
    return (
      <Badge variant="brand" size="small">
        {count}
        <VisuallyHidden>
          {includesMentions
            ? ' unanswered comments and mentions'
            : ' unreplied comments'}
        </VisuallyHidden>
      </Badge>
    )
  }

  // Priority 3: No suffix
  return undefined
}

export function ChannelSubNav({
  channel,
  handleNavigation,
}: ChannelSubNavProps): JSX.Element {
  const organizationId = useOrganizationId()
  const isOneBufferOrganization = useIsOneBufferOrg()
  const { isChannelSupported, loading: countsLoading } =
    useCommunityCountsContext()
  const { getChannelTotal, includesMentions } = useCommunityCounter()
  const {
    config: { channelNeedsRefresh },
  } = useCommunityConfig()

  const scheduleUrl = useChannelUrl(channel.id)
  const commentsUrl = useCommentsChannelUrl(channel.id)
  const insightsUrl = useInsightsUrl(channel.id)

  const location = useLocation()
  const isCommunityActive = location.pathname.startsWith(
    `/channels/${channel.id}/community`,
  )

  const handleSubNavClick = (): void => {
    // Sub-item navigation: expand this channel and collapse others
    handleNavigation(channel.id)
  }

  // Fetch post counts for this specific channel
  const { counts: postCounts } = usePostCounts({
    organizationId: organizationId ?? undefined,
    channelIds: [channel.id],
  })

  const queueCount = postCounts?.queue ?? 0
  const queueCountLimit = postCounts?.limit

  // Calculate conditional flags
  const isSupportedInComments = isChannelSupported(channel)
  const isStartPage = channel.service === 'startPage'

  // Effective community count for the badge: unreplied comments, plus unanswered
  // mentions in counter mode (see useCommunityCounter). 0 when unsupported.
  const communityCount = isSupportedInComments ? getChannelTotal(channel.id) : 0

  return (
    <Sidebar.List
      aria-label={`${getChannelNameForDisplay(channel)} pages`}
      className={styles.subNavList}
    >
      {/* Schedule - always shown */}
      <Sidebar.ListItem className={styles.subNavListItemFirst}>
        <Sidebar.Button
          as={NavLink}
          to={scheduleUrl}
          indent={1}
          className={styles.subNavLink}
          onClick={handleSubNavClick}
          prefix={<CalendarIcon />}
          suffix={
            queueCount > 0 && (
              <>
                {formatCompactNumber(queueCount, queueCountLimit)}
                <VisuallyHidden> scheduled posts</VisuallyHidden>
              </>
            )
          }
          data-subnav-item
        >
          Publish
        </Sidebar.Button>
      </Sidebar.ListItem>

      {/* Community - only shown for One Buffer organisations */}
      {isOneBufferOrganization && (
        <Sidebar.ListItem className={styles.subNavListItem}>
          <Sidebar.Button
            as={NavLink}
            to={commentsUrl}
            selected={isCommunityActive}
            indent={1}
            className={styles.subNavLink}
            onClick={handleSubNavClick}
            prefix={<MessagesSquareIcon />}
            suffix={getCommunitySuffix({
              isLoading: countsLoading,
              count: communityCount,
              includesMentions,
              needsRefresh: channelNeedsRefresh(channel),
            })}
            data-subnav-item
          >
            Community
          </Sidebar.Button>
        </Sidebar.ListItem>
      )}

      {/* Insights */}
      <Sidebar.ListItem className={styles.subNavListItem}>
        <Sidebar.Button
          as={NavLink}
          to={insightsUrl}
          indent={1}
          className={styles.subNavLink}
          onClick={handleSubNavClick}
          prefix={<ChartColumnIcon />}
          suffix={<NewBadge />}
          data-subnav-item
        >
          Insights
        </Sidebar.Button>
      </Sidebar.ListItem>

      {/* Editor - external link to Start Page */}
      {isStartPage && (
        <Sidebar.ListItem className={styles.subNavListItem}>
          <Sidebar.Button
            as="a"
            href={buildStartPageEditorUrl(channel.id)}
            target="_blank"
            rel="noopener noreferrer"
            indent={1}
            className={styles.subNavLink}
            prefix={<PaintBucketIcon />}
            suffix={<ExternalLinkIcon size="xsmall" color="subtle" />}
            data-subnav-item
          >
            Editor
          </Sidebar.Button>
        </Sidebar.ListItem>
      )}
    </Sidebar.List>
  )
}
